import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { Activity, ArrowRight, Calendar, Sparkles, Users } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { useGroupsList, useCandidatesList, useSessionsForGroup, useEffectiveUser } from "@/store/hooks";

export default function InstructorDashboard() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("instructor"), [setRole]);
  const groups = useGroupsList();
  const candidates = useCandidatesList();
  const me = useEffectiveUser();

  const myGroup = useMemo(() => {
    const mine = me ? groups.find((g) => g.instructorId === me.id) : undefined;
    return mine ?? groups.find((g) => g.estado === "en-curso") ?? groups[0];
  }, [groups, me]);

  const sessions = useSessionsForGroup(myGroup?.id);
  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...sessions].filter((s) => s.fecha >= today).sort((a, b) => (a.fecha + a.hora).localeCompare(b.fecha + b.hora)).slice(0, 4);
  }, [sessions]);

  if (!myGroup) {
    return (
      <AppLayout pageTitle="Panel del instructor">
        <Card><div style={{ padding: 16, fontSize: 13 }}>No tiene grupos asignados.</div></Card>
      </AppLayout>
    );
  }

  const list = candidates.filter((c) => myGroup.candidatosIds.includes(c.id));

  return (
    <AppLayout pageTitle="Panel del instructor">
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Mi grupo activo — {myGroup.nombre}</h1>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{list.length} candidatos · {myGroup.inicio} → {myGroup.fin}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card padding={0}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
            <SectionTitle>Avance del grupo</SectionTitle>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
                {["Candidato", "Avance", "Estado", "Última actividad", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 18, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13 }}>Sin candidatos inscritos en este grupo.</td></tr>
              )}
              {list.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{c.iniciales}</div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", width: 180 }}>
                    <div style={{ background: "var(--cc-gray-100)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                      <div style={{ background: "var(--cc-teal)", width: `${c.progreso}%`, height: "100%" }} />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>{c.progreso}%</div>
                  </td>
                  <td style={{ padding: "10px 14px" }}><Badge tone={c.estado === "certificado" ? "green" : "teal"}>{c.estado.replace("-", " ")}</Badge></td>
                  <td style={{ padding: "10px 14px", fontSize: 11, color: "var(--cc-gray-500)" }}>{c.ultimaActividad}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <Link href={`/instructor/grupos/${myGroup.id}`} style={{ color: "var(--cc-teal)", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>Ver →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Próximas sesiones</SectionTitle>
            {upcoming.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)", padding: 6 }}>Sin sesiones futuras programadas.</div>
            )}
            {upcoming.map((s) => (
              <Item key={s.id} icon={s.tipo === "practica" ? Activity : s.tipo === "cierre" ? Users : Calendar} title={s.titulo} sub={`${s.fecha} · ${s.hora}`} />
            ))}
          </Card>

          <Link href={`/instructor/grupos/${myGroup.id}`} data-testid="link-manage-group">
            <PrimaryButton>Gestionar mi grupo</PrimaryButton>
          </Link>
          <Link href="/instructor/grupos" data-testid="link-all-groups" style={{ fontSize: 12, color: "var(--cc-teal)", fontWeight: 600, textDecoration: "none", textAlign: "center" }}>
            Ver todos mis grupos →
          </Link>

          <Link href="/instructor/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
            <Card style={{ cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Sparkles size={15} color="var(--cc-teal)" />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>Pregúntale a CertiAI</span>
                <ArrowRight size={13} color="var(--cc-teal)" style={{ marginLeft: "auto" }} />
              </div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>Quién va atrasado, asistencia y próximas sesiones.</div>
            </Card>
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}

function Item({ icon: Icon, title, sub }: { icon: React.ComponentType<{ size?: number; color?: string }>; title: string; sub: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <Icon size={15} color="var(--cc-teal)" />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{sub}</div>
      </div>
    </div>
  );
}
